/**
 * 系统管理模块权限码常量
 *
 * ⚠️ Code alignment ADR (REQ-20260521-0011 task #11889):
 *
 * 历史上前端用 `system:<resource>:<create|update|delete>` 三段式细粒度 code，
 * 但后端 `fnd_permissions` 表实际 seed 的是 `tenant:<resource>:<view|manage>`
 * 二段式粗粒度 code（资源名也不一定一致：FE `organizations` vs DB `department`）。
 * 这导致 `<Permission>` 守卫永远 false → 普通租户 admin 看不到 CRUD 按钮，
 * 只能依赖 `isSuperAdmin()` bypass。详见 `web/scripts/audit-perm-codes.md`。
 *
 * **政策决定**（@PM 决策 msg `fcc5bcf6`）：
 *   - Frontend 改用 `tenant:<resource>:<view|manage>` 跟 DB seed 对齐；
 *   - 同一资源的 CRUD 共用一个 `:manage`，不再做 create/update/delete 细分；
 *   - **本 task 只迁 `roles`**，其他 `system:*` legacy 留在下面 // LEGACY 标注里
 *     等下期 REQ 分批迁移（用 grep 找现役 import 后处理）。
 *
 * **新 code 约定**（going forward）:
 *   - 只读：`tenant:<resource>:view`
 *   - CRUD：`tenant:<resource>:manage`
 *   - 不要再新增 `system:*` 前缀 code（违反对齐策略）
 *
 * 删除 LEGACY 标注前请先 grep 校确 0 import：`grep -r PERM_XXX web/src`。
 */

// ───── 角色管理 — REQ-20260521-0011 task #11889 迁移完成 ─────
// 跟 DB seed `tenant:role:view` (id=13) / `tenant:role:manage` (id=14) 对齐
export const PERM_ROLE_VIEW = 'tenant:role:view';
export const PERM_ROLE_MANAGE = 'tenant:role:manage';

// LEGACY 别名（保持向后兼容；新代码请用 PERM_ROLE_MANAGE）
// 三个常量同一字符串值，让既有 `<Permission permission={PERM_ROLES_UPDATE}>`
// 等用法在 grep + 替换前还能跑通 + 命中 DB seed。
export const PERM_ROLES_CREATE = PERM_ROLE_MANAGE;
export const PERM_ROLES_UPDATE = PERM_ROLE_MANAGE;
export const PERM_ROLES_DELETE = PERM_ROLE_MANAGE;

// ───── LEGACY: 下列 code 尚未对齐 DB（已知 drift，REQ-20260521-0011 task #11889 范围外）─────
// audit 报告: web/scripts/audit-perm-codes.md

// 用户管理 (LEGACY)
export const PERM_USERS_CREATE = 'system:users:create';
export const PERM_USERS_UPDATE = 'system:users:update';
export const PERM_USERS_DELETE = 'system:users:delete';

// 权限资源管理 (LEGACY)
export const PERM_PERMISSIONS_CREATE = 'system:permissions:create';
export const PERM_PERMISSIONS_UPDATE = 'system:permissions:update';
export const PERM_PERMISSIONS_DELETE = 'system:permissions:delete';

// 组织架构管理 (LEGACY — resource-name drift "organizations" vs DB "department")
export const PERM_ORGS_CREATE = 'system:organizations:create';
export const PERM_ORGS_UPDATE = 'system:organizations:update';
export const PERM_ORGS_DELETE = 'system:organizations:delete';

// 菜单管理 (LEGACY — DB seed 完全缺这些 code，需后续 REQ 决定加 seed 还是改 FE)
export const PERM_MENUS_CREATE = 'system:menus:create';
export const PERM_MENUS_UPDATE = 'system:menus:update';
export const PERM_MENUS_DELETE = 'system:menus:delete';
